import { FileEdit, Ban, FileCheck, FileSpreadsheet, FileText, Wallet, Lock, Trash2 } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { useState } from 'react';
import RegulModal from './RegulModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface TransactionActionsProps {
  transaction: Transaction;
  isAdmin: boolean;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  onValidate: (transaction: Transaction) => void;
  onCancel: (transaction: Transaction) => void;
  onCompromise: (transaction: Transaction, compromiseDate: string, expectedActeDate: string) => void;
  onActeTransaction?: (transaction: Transaction) => void;
  onEncaissementTransaction?: (transaction: Transaction) => void;
  onClotureTransaction?: (transaction: Transaction) => void;
  onRegulTransaction?: (transaction: Transaction) => void;
}

export default function TransactionActions({
  transaction,
  isAdmin,
  onEdit,
  onDelete,
  onValidate,
  onCancel,
  onCompromise,
  onActeTransaction,
  onEncaissementTransaction,
  onClotureTransaction,
  onRegulTransaction
}: TransactionActionsProps) {
  const [isRegulModalOpen, setIsRegulModalOpen] = useState(false);
  const { user } = useAuth();

  const userIsAdmin = user?.role === 'admin';

  const canEdit = userIsAdmin || transaction.status === 'potentiel';
  const canDelete = transaction.status === 'potentiel';
  const canCancel = false;
  const canCompromise = transaction.status === 'potentiel';
  const canActe = userIsAdmin && transaction.status === 'compromis' && !!onActeTransaction;
  const canEncaissement = userIsAdmin && transaction.status === 'acte' && !!onEncaissementTransaction;
  const canCloture = userIsAdmin && transaction.status === 'encaisse' && !!onClotureTransaction;
  const canRequestRegul =
    ['compromis', 'acte', 'encaisse', 'cloture'].includes(transaction.status) &&
    !transaction.regul &&
    !!onRegulTransaction;

  const handleRegulRequest = async (comment: string) => {
    try {
      const { error: updateError } = await supabase
        .from('transactions')
        .update({ regul: true, commentaire_regul: comment })
        .eq('id', transaction.id);

      if (updateError) throw updateError;

      const { error: requestError } = await supabase
        .from('modification_requests')
        .insert([{
          transaction_id: transaction.id,
          requested_by: user?.id,
          status: 'pending',
          changes_requested: { message: comment },
          request_status: 'submitted'
        }]);

      if (requestError) throw requestError;

      setIsRegulModalOpen(false);
      window.location.reload();
    } catch (err) {
      console.error('Error requesting regul:', err);
    }
  };

  const btnCls = 'p-1.5 rounded-lg transition-colors';

  // Profil lecture seule : aucune action possible
  if (user?.role === 'lecture') return null;

  return (
    <div className="flex items-center gap-1.5">
      {canEdit && (
        <button onClick={() => onEdit(transaction)} className={`${btnCls} bg-indigo-50 text-indigo-600 hover:bg-indigo-100`} title="Modifier">
          <FileEdit size={15} />
        </button>
      )}

      {canDelete && (
        <button
          onClick={() => {
            if (confirm('Êtes-vous sûr de vouloir supprimer cette transaction ? Cette action est irréversible.')) {
              onDelete(transaction);
            }
          }}
          className={`${btnCls} bg-red-50 text-red-500 hover:bg-red-100`}
          title="Supprimer"
        >
          <Trash2 size={15} />
        </button>
      )}

      {canCancel && (
        <button onClick={() => onCancel(transaction)} className={`${btnCls} bg-red-50 text-red-500 hover:bg-red-100`} title="Annuler">
          <Ban size={15} />
        </button>
      )}

      {canCompromise && (
        <button
          onClick={() => { console.log('[TransactionActions] Opening compromise modal for transaction:', transaction.property_number); onCompromise(transaction); }}
          className={`${btnCls} bg-amber-50 text-amber-600 hover:bg-amber-100`}
          title="Passer en compromis"
        >
          <FileCheck size={15} />
        </button>
      )}

      {canRequestRegul && (
        <button
          onClick={() => onRegulTransaction!(transaction)}
          className={`${btnCls} bg-violet-50 text-violet-600 hover:bg-violet-100`}
          title={transaction.commentaire_regul ? `Régularisation : ${transaction.commentaire_regul}` : 'Demande de régularisation'}
        >
          <FileSpreadsheet size={15} />
        </button>
      )}

      {canActe && (
        <button onClick={() => onActeTransaction!(transaction)} className={`${btnCls} bg-blue-50 text-blue-600 hover:bg-blue-100`} title="Passer en acte">
          <FileText size={15} />
        </button>
      )}

      {canEncaissement && (
        <button onClick={() => onEncaissementTransaction!(transaction)} className={`${btnCls} bg-emerald-50 text-emerald-600 hover:bg-emerald-100`} title="Passer en encaissé">
          <Wallet size={15} />
        </button>
      )}

      {canCloture && (
        <button onClick={() => onClotureTransaction!(transaction)} className={`${btnCls} bg-slate-100 text-slate-600 hover:bg-slate-200`} title="Clôturer">
          <Lock size={15} />
        </button>
      )}

      <RegulModal
        transaction={transaction}
        isOpen={isRegulModalOpen}
        onClose={() => setIsRegulModalOpen(false)}
        onSubmit={handleRegulRequest}
      />
    </div>
  );
}
